import { useNavigate } from 'react-router-dom'

import { useDeleteOngoingAssessment } from '@/api/assessments.hook'
import { useUpdateCompany } from '@/api/company.hook'
import { CompanyStatus } from '@/gen/inventory/v1/company_status_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { AppRoute } from '@/lib/url/urls'

import { useCompanyHasAssessments } from '@/pages/assessment/ag-grid-assessment/config/use-company-has-assessments'
import { useAssessmentCtx } from '@/pages/assessment/assessment-context'

import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

interface DeleteAssessmentModalProps {
  redirectOnDelete?: boolean
}

export const DeleteAssessmentModal = ({ redirectOnDelete = false }: DeleteAssessmentModalProps) => {
  const navigate = useNavigate()
  const { isOpen, setIsOpen, assessmentId, companyId } = useAssessmentCtx()
  const { toast } = useToast()
  const trackAssessmentDelete = useTrackCallback('third-party.assessment.delete')
  const { mutate: updateCompany } = useUpdateCompany(companyId)

  const { hasOngoingAssessments, noOngoingAssessments, noPastAssessments } =
    useCompanyHasAssessments(companyId)
  const { mutateAsync: deleteAssessment, isPending } = useDeleteOngoingAssessment()

  const shouldRecalculateStatus =
    hasOngoingAssessments && noOngoingAssessments == 1 && noPastAssessments > 0

  return (
    <Modal
      open={isOpen}
      size='small'
      variant='destructive'
      title='Delete assessment'
      subtitle='Deleting this assessment is permanent. All questionnaires and responses will be permanently removed. Are you sure you want to continue?'
      onCancel={() => setIsOpen(false)}
      onOk={async () => {
        await deleteAssessment(
          { ongoingAssessmentId: assessmentId },
          {
            onSuccess: () => {
              trackAssessmentDelete({
                ongoingAssessmentId: assessmentId,
                companyId,
              })
              toast({
                variant: 'success',
                title: 'Assessment deleted successfully',
              })
              if (shouldRecalculateStatus) {
                updateCompany({
                  id: companyId,
                  status: CompanyStatus.APPROVED,
                })
              }
              setIsOpen(false)
              if (redirectOnDelete) {
                navigate(AppRoute.ASSESSMENTS)
              }
            },
            onError: (error) => {
              console.error('Failed to delete assessment:', error)
              toast({
                variant: 'destructive',
                title: 'Failed to delete assessment',
                description: 'Please try again later.',
              })
            },
          },
        )
      }}
      confirmLoading={isPending}
    />
  )
}
